import { LinearGradient } from 'expo-linear-gradient';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, Dimensions } from 'react-native';

import {
  formatYen,
  getNumberRoundedToTens,
  getNumberRoundedToTensInJapanese,
} from '../utils';
import { MONTHLY_USAGE_BAR_MIN_MONTHS } from '../utils';

import {
  useBillStatementsQuery,
  useConsumerUsageSummaryQuery,
} from 'apollo/queries/autogenerate/hooks';
import { billStatementFragment } from 'apollo/queries/autogenerate/operations';
import {
  BillStatement,
  BillStatementStatusV2,
  TotalMonthUsage,
} from 'apollo/queries/autogenerate/schemas';
import colors from 'constants/colors';

const USAGE_BAR_MAX_HEIGHT = 90;
const USAGE_BAR_MIN_HEIGHT = 2;

const getMonthYearFromBillingCycle = (billingCycle: string) => {
  // billingCycle format: '2021-05'
  const [year, month] = billingCycle.split('-').map(Number);

  return { month, year };
};

const getColor = (billingCycle: string) => {
  const currentMonth = moment().tz('Asia/Tokyo');
  const usageMonth = moment(billingCycle).tz('Asia/Tokyo');

  const shouldHighLight =
    usageMonth.isBefore(currentMonth) || usageMonth === currentMonth;

  return shouldHighLight
    ? { text: colors.greyDark, bar: [colors.pink, colors.purple] }
    : { text: colors.greyMedium, bar: ['#F2D0E2', '#F2D0E2'] };
};

const getBillsByBillingCycle = (bills: BillStatement[] | undefined) => {
  const billsByBillingCycle: Record<string, billStatementFragment> = {};

  if (bills) {
    bills.forEach((bill) => {
      const billingCycle = moment(bill.period.end).format('YYYY-MM');

      billsByBillingCycle[billingCycle] = bill;
    });
  }

  return billsByBillingCycle;
};

/*
  4 bars should be displayed as default even the monthlyUsageData we received is less than 4
  The below line of codes creates missing months with 0 yen usage
*/
export const supplyMissingUsages = (usages: TotalMonthUsage[] | undefined) => {
  if (!usages) {
    return [];
  }

  const monthlyUsageData = usages.map((elem) => elem);

  if (
    monthlyUsageData &&
    monthlyUsageData?.length < MONTHLY_USAGE_BAR_MIN_MONTHS
  ) {
    const noOfMissingMonths =
      MONTHLY_USAGE_BAR_MIN_MONTHS - monthlyUsageData?.length;
    const lastItem = monthlyUsageData[monthlyUsageData.length - 1];

    for (let i = 0; i < noOfMissingMonths; i++) {
      const billingCycle = moment(lastItem.billingCycle)
        .add(i + 1, 'months')
        .format('YYYY-MM')
        .split('-');

      if (billingCycle && billingCycle.length > 1) {
        monthlyUsageData.push({
          ...lastItem,
          usage: 0,
          billingCycle: billingCycle[0] + '-' + billingCycle[1],
        });
      }
    }
  }

  return monthlyUsageData;
};

export default function MonthlyUsageBars({
  monthlyUsageBarScrollviewRef,
  onScrollBeginDrag,
  onScrollEndDrag,
}: {
  monthlyUsageBarScrollviewRef?: any;
  onScrollBeginDrag?: () => void;
  onScrollEndDrag?: () => void;
}) {
  const { data: usageSummaryData } = useConsumerUsageSummaryQuery();

  const largestUsageAmount: number = useMemo(() => {
    if (!usageSummaryData) {
      return 1;
    }

    let biggestUsage = 1;

    usageSummaryData.consumerUsageSummary.monthlyTotalUsagesV3.forEach(
      ({ usage }) => {
        if (usage > biggestUsage) {
          biggestUsage = usage;
        }
      }
    );

    return biggestUsage;
  }, [usageSummaryData]);

  const {
    upperRangeInJapanese,
    midRangeInJapanese,
  } = getNumberRoundedToTensInJapanese(largestUsageAmount);

  const upperRange = getNumberRoundedToTens(largestUsageAmount);

  const monthlyUsageData = supplyMissingUsages(
    usageSummaryData?.consumerUsageSummary?.monthlyTotalUsagesV3
  );

  return usageSummaryData ? (
    <>
      <View>
        <ScrollView
          horizontal={true}
          style={[
            styles.container,
            largestUsageAmount === 1 && { marginLeft: 0 },
          ]}
          contentContainerStyle={styles.scrollContainer}
          ref={monthlyUsageBarScrollviewRef}
          scrollEnabled={monthlyUsageData.length > MONTHLY_USAGE_BAR_MIN_MONTHS}
          showsHorizontalScrollIndicator={false}
          onScrollBeginDrag={onScrollBeginDrag}
          onScrollEndDrag={onScrollEndDrag}
        >
          {monthlyUsageData &&
            monthlyUsageData.map(({ usage, billingCycle }, index) => {
              return (
                <MonthlyBar
                  usage={usage}
                  billingCycle={billingCycle}
                  index={index}
                  upperRange={upperRange}
                  key={index}
                />
              );
            })}

          <View style={styles.baseLine} />
        </ScrollView>
        {largestUsageAmount !== 1 && (
          <>
            <Text
              style={[
                styles.range,
                {
                  top: -3,
                },
              ]}
            >
              {upperRangeInJapanese}
            </Text>

            <Text
              style={[
                styles.range,
                {
                  top: USAGE_BAR_MAX_HEIGHT / 2 - 3,
                },
              ]}
            >
              {midRangeInJapanese}
            </Text>

            <Text
              style={[
                styles.range,
                {
                  top: USAGE_BAR_MAX_HEIGHT - 3,
                },
              ]}
            >
              {` 0`}
            </Text>
          </>
        )}
      </View>
    </>
  ) : null;
}

const MonthlyBar = ({
  usage,
  billingCycle,
  upperRange,
  index,
}: {
  usage: number;
  billingCycle: string;
  index: number;
  upperRange: number;
}) => {
  const { month } = getMonthYearFromBillingCycle(billingCycle);

  const { data: bills } = useBillStatementsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const billsByBillingCycle = useMemo(
    () => getBillsByBillingCycle(bills?.billStatements as BillStatement[]),
    [bills]
  );

  const color = getColor(billingCycle);
  const usageBarHeight = Math.floor(
    (usage / upperRange) * USAGE_BAR_MAX_HEIGHT
  );

  const isBillOverdue =
    billsByBillingCycle[billingCycle]?.statusV2 ===
    BillStatementStatusV2.OVERDUE;

  // Display only 4 bars to scrollview, Bar width formula is screen width divide by 4 then minus the margin
  // Margin can be found below (container and monthlyUsageContainer)
  const PARENT_MARGIN = 40;
  const monthlyUsageWidth =
    Dimensions.get('window').width / MONTHLY_USAGE_BAR_MIN_MONTHS -
    PARENT_MARGIN;

  return (
    <View
      style={[styles.monthlyUsageContainer, { width: monthlyUsageWidth }]}
      key={index}
    >
      <View style={styles.monthlyUsageBar}>
        <LinearGradient
          colors={color.bar}
          style={[
            styles.monthlyUsageAmount,
            {
              height:
                usageBarHeight < USAGE_BAR_MIN_HEIGHT
                  ? USAGE_BAR_MIN_HEIGHT
                  : usageBarHeight,
              borderRadius: 4,
            },
          ]}
        />
      </View>
      <Text style={[styles.monthlyUsageText, { color: color.text }]}>
        {month}月分
      </Text>
      <Text style={[styles.monthlyUsageText, { color: color.text }]}>
        {formatYen(usage)}
      </Text>
      {isBillOverdue && <Text style={styles.overdueText}>お支払い待ち</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 150,
    marginLeft: 20,
  },
  monthlyUsageContainer: {
    alignItems: 'center',
    flex: 1,
    marginRight: 20,
  },
  monthlyUsageBar: {
    height: USAGE_BAR_MAX_HEIGHT,
    justifyContent: 'flex-end',
    marginBottom: 8,
  },
  monthlyUsageAmount: {
    width: 12,
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
  },
  monthlyUsageText: {
    fontSize: 10,
    fontWeight: 'bold',
    lineHeight: 16,
  },
  overdueText: {
    color: colors.red,
    fontSize: 10,
    fontWeight: 'bold',
    lineHeight: 16,
  },
  baseLine: {
    height: 1,
    width: '100%',
    position: 'absolute',
    bottom: 60,
    backgroundColor: colors.greyLight,
  },
  range: {
    position: 'absolute',
    fontSize: 8,
    color: colors.greyMedium,
    fontWeight: '600',
  },
  scrollContainer: {
    flexGrow: 1,
  },
});
